// Central demo state + a tiny event bus. Vanilla, no framework.

const listeners = new Map();

export function on(event, fn) {
  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event).add(fn);
  return () => listeners.get(event)?.delete(fn);
}

export function emit(event, payload) {
  listeners.get(event)?.forEach(fn => {
    try { fn(payload); } catch (e) { console.error(`listener for ${event} failed`, e); }
  });
}

export const state = {
  // Selected areas, keyed by NIS9 to dedupe. Each: { nis9, postcode, municipality, totalHH, feature }
  sectors: new Map(),

  // Postcodes to count directly (from the DTM radius/drive-time result), independent of the
  // slow per-sector postcode resolution. Counting uses these ∪ resolved-sector postcodes,
  // so a big radius/drive-time selection gets fast, correct totals + email immediately.
  pcUnits: new Set(),

  // Selected Dividuals subsegment codes (e.g. "D11"). Drives the live count.
  dividuals: new Set(),

  // Friendly-picker selection. The pickers do NOT filter counts directly — they compute
  // the corresponding Dividual subsegments (dividualMapping.js), which are pre-checked and
  // then drive counts. Age domain 18..90 (full = no constraint); income levels 0..2.
  friendly: { familySizes: new Set(), houseTypes: new Set(), ageRange: [18, 90], incomeRange: [25, 200] },

  // Minimum target-group percentage (slider). Minimum enforced at 5.
  minTargetPct: 0,

  includeSegmentedEmail: false,

  // Target-group language for The Wave reach figures only: null (all) | 'nl' | 'fr'. It never
  // touches the GIS addresses or the per-zone percentage; it is a per-block `lang` filter.
  waveLang: null,

  // Count results from The Wave (or GIS), keyed by count unit: the NIS9 in sector mode, the
  // postcode in postcode mode (see countUnits() in main.js). Each:
  // { total, target, pct, emailTotal, emailTarget, phoneTotal, phoneTarget }
  counts: new Map(),
  countUnit: 'postcode', // 'sector' | 'postcode' — what the keys of `counts` are
  // Target share per sector from Sevenn (details per NIS9), only for sectors it has addresses
  // for. Drives the threshold per sector; sectors without an entry use their count unit's %.
  sectorPct: new Map(),

  loadingCounts: false
};

// The key in state.counts that holds a sector's reach figures: its own NIS9 in sector mode,
// its postcode in postcode mode. null when nothing can be looked up yet.
export function countKeyOf(sector) {
  if (state.countUnit === 'sector') return sector.nis9;
  return sector.postcode ? String(sector.postcode) : null;
}

// The target % that decides whether a sector survives the threshold (null = not known yet).
export function sectorPctOf(sector) {
  const own = state.sectorPct.get(sector.nis9);
  if (own != null) return own;
  const k = countKeyOf(sector);
  const c = k ? state.counts.get(String(k)) : null;
  return c && typeof c.pct === 'number' ? c.pct : null;
}

export function clearSelection() {
  state.sectors.clear();
  state.pcUnits.clear();
  state.counts.clear();
  state.sectorPct.clear();
  emit('sectors-changed');
  emit('counts-changed');
}
